import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';

// Depreciation Types
export interface DepreciationParameters {
  asset_id: string;
  depreciation_rate: number;
  depreciation_start_date: string;
  expected_end_date: string;
  residual_value: number;
  accumulated_depreciation_opening_balance: number;
}

export interface DepreciationCalculation {
  id: string;
  asset: string;
  asset_detail: {
    id: string;
    asset_tag: string;
    asset_name: string;
    purchase_value: string;
  };
  calculation_date: string;
  depreciable_amount: number;
  annual_depreciation: number;
  daily_depreciation_rate: number;
  depreciation_for_date: number;
  accumulated_depreciation: number;
  net_book_value: number;
  created_at: string;
}

export interface DepreciationParametersPreview {
  depreciationRate: number;
  depreciationStartDate: string;
  expectedEndDate: string;
  residualValue: number;
  accumulatedDepreciationOpeningBalance: number;
}

export interface DepreciationCalculationPreview {
  assetId: string;
  assetCost: number;
  residualValue: number;
  depreciableAmount: number;
  calculationDate: string;
  depreciationRate: number;
  annualDepreciation: number;
  dailyDepreciation: number;
  reportDate: string;
  elapsedDays: number;
  periodDepreciation: number;
  accumulatedDepreciation: number;
  netBookValue: number;
}

export interface DepreciationDifferencesPreview {
  depreciableAmount: number;
  annualDepreciation: number;
  dailyDepreciation: number;
  elapsedDays: number;
  periodDepreciation: number;
  accumulatedDepreciation: number;
  netBookValue: number;
}

export interface AssetDepreciationReport {
  assetId: string;
  assetName: string;
  reportDate: string;
  oldParameters: DepreciationParametersPreview;
  newParameters: DepreciationParametersPreview;
  oldCalculation: DepreciationCalculationPreview;
  newCalculation: DepreciationCalculationPreview;
  differences: DepreciationDifferencesPreview;
}

export interface AssetDepreciationReportPreview {
  data: AssetDepreciationReport;
  message: string;
  success: boolean;
}

export interface NBVHistory {
  id: string;
  asset: string;
  asset_detail: {
    id: string;
    asset_tag: string;
    asset_name: string;
  };
  calculation_date: string;
  net_book_value: number;
  accumulated_depreciation: number;
  created_at: string;
}

export interface DepreciationParametersHistory {
  id: string;
  asset: string;
  asset_detail: {
    id: string;
    asset_tag: string;
    asset_name: string;
  };
  field_name: string;
  old_value: string | null;
  new_value: string;
  changed_at: string;
  changed_by: string;
  changed_by_detail: {
    id: string;
    name: string;
    email: string;
  };
  change_reason: string;
}

export interface DisposalCalculation {
  asset_id: string;
  disposal_date: string;
  disposal_value: number;
  accumulated_depreciation: number;
  net_book_value: number;
  gain_loss_on_disposal: number;
  disposal_type: 'full' | 'partial';
  disposal_percentage?: number;
}

export interface DepreciationReport {
  report_type: string;
  generated_at: string;
  filters: Record<string, any>;
  data: any[];
  summary: {
    total_assets: number;
    total_depreciation: number;
    total_nbv: number;
  };
}

// Bulk operations
export interface BulkDepreciationUpdate {
  asset_ids: string[];
  parameters: Partial<DepreciationParameters>;
}

export interface BulkDepreciationPreview {
  asset_id: string;
  asset_tag: string;
  asset_name: string;
  current_parameters: DepreciationParameters;
  new_parameters: DepreciationParameters;
  impact: {
    nbv_change: number;
    depreciation_change: number;
  };
}

// Category Depreciation Types
export interface CategoryDepreciationParameters {
  id: string;
  name: string;
  default_depreciation_rate: number | null;
  default_useful_life_years: number | null;
  default_residual_value_percentage: number | null;
}

export interface CategoryDepreciationSummary {
  category: CategoryDepreciationParameters;
  assets_summary: {
    total_assets: number;
    assets_using_category_rate: number;
    assets_with_custom_rate: number;
    assets_without_rate: number;
  };
}

export interface EffectiveDepreciationParameters {
  asset_id: string;
  asset_tag: string;
  asset_name: string;
  category_name: string | null;
  effective_depreciation_rate: number;
  effective_residual_value: number;
  effective_expected_end_date: string | null;
  parameter_sources: {
    depreciation_rate_source: 'asset' | 'category' | 'none';
    residual_value_source: 'asset' | 'category' | 'none';
    expected_end_date_source: 'asset' | 'category' | 'none';
  };
  category_defaults: CategoryDepreciationParameters | null;
}

export interface CategoryDefaultsApplication {
  total_assets: number;
  updated_assets: number;
  skipped_assets: number;
  errors: any[];
  details: any[];
}

interface SelectOption {
  label: string;
  value: string;
}

// Create API
export const depreciationApi = createApi({
  reducerPath: 'depreciationApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['DepreciationParameters', 'DepreciationCalculation', 'NBVHistory', 'DepreciationReport', 'CategoryDepreciation', 'DepreciationStatus'],
  keepUnusedDataFor: 120,
  endpoints: (builder) => ({
    // Asset Parameters Management
    getAssetDepreciationParameters: builder.query<DepreciationParameters, string>({
      query: (assetId) => `assets/depreciations/assets/${assetId}/depreciation-parameters/`,
      providesTags: (result, error, assetId) => [{ type: 'DepreciationParameters', id: assetId }],
      transformResponse: (response: any) => {
        if (response?.success && response?.data) {
          const data = response.data;
          return {
            asset_id: data.assetId,
            depreciation_rate: data.depreciationRate,
            depreciation_start_date: data.depreciationStartDate,
            expected_end_date: data.expectedEndDate,
            residual_value: data.residualValue,
            depreciation_status: data.depreciationStatus,
            accumulated_depreciation_opening_balance: data.accumulatedDepreciationOpeningBalance,
          };
        }
        return response;
      },
    }),

    updateAssetDepreciationParameters: builder.mutation<DepreciationParameters, { assetId: string; parameters: Partial<DepreciationParameters> }>({
      query: ({ assetId, parameters }) => ({
        url: `assets/depreciations/assets/${assetId}/depreciation-parameters/`,
        method: 'PUT',
        body: parameters,
      }),
      invalidatesTags: (result, error, { assetId }) => [
        { type: 'DepreciationParameters', id: assetId },
        { type: 'DepreciationCalculation', id: assetId },
        { type: 'NBVHistory', id: assetId },
      ],
    }),

    bulkUpdateDepreciationParameters: builder.mutation<any, BulkDepreciationUpdate>({
      query: (data) => ({
        url: 'assets/depreciations/assets/bulk/depreciation-parameters/',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['DepreciationParameters', 'DepreciationCalculation', 'NBVHistory'],
    }),

    previewBulkDepreciationUpdate: builder.mutation<BulkDepreciationPreview[], BulkDepreciationUpdate>({
      query: (data) => ({
        url: 'assets/depreciations/assets/bulk/depreciation-parameters/preview/',
        method: 'POST',
        body: data,
      }),
    }),

    getDepreciationParametersHistory: builder.query<{ results: DepreciationParametersHistory[] }, { assetId?: string; page?: number; pageSize?: number }>({
      query: ({ assetId, page = 1, pageSize = 10 }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          page_size: pageSize.toString(),
        });
        if (assetId) params.append('asset_id', assetId);
        return `assets/depreciations/parameters/history/?${params}`;
      },
      transformResponse: (response: any) => {
        if (response?.success && response?.data?.records && Array.isArray(response.data.records)) {
          const flattenedRecords: DepreciationParametersHistory[] = [];
          
          response.data.records.forEach((record: any) => {
            const { previousValues, newValues, createdAt, createdBy, changeReason, assetId } = record;
            
            // Compare each field and create separate records for changed fields
            const fieldMappings = {
              depreciationRate: 'Depreciation Rate',
              depreciationStartDate: 'Depreciation Start Date',
              expectedEndDate: 'Expected End Date',
              residualValue: 'Residual Value',
              accumulatedDepreciationOpeningBalance: 'Opening Balance'
            };
            
            Object.entries(fieldMappings).forEach(([apiField, displayName]) => {
              const oldValue = previousValues?.[apiField];
              const newValue = newValues?.[apiField];
              
              // Only create a record if the value actually changed
              if (oldValue !== newValue) {
                flattenedRecords.push({
                  id: `${record.id}-${apiField}`,
                  asset: assetId,
                  asset_detail: {
                    id: assetId,
                    asset_tag: '',
                    asset_name: '',
                  },
                  field_name: displayName,
                  old_value: oldValue?.toString() || null,
                  new_value: newValue?.toString() || '',
                  changed_at: createdAt,
                  changed_by: createdBy?.id || '',
                  changed_by_detail: {
                    id: createdBy?.id || '',
                    name: createdBy?.name || '',
                    email: createdBy?.email || createdBy?.name || '',
                  },
                  change_reason: changeReason || '',
                });
              }
            });
          });
          
          return { results: flattenedRecords };
        }
        return { results: [] };
      },
    }),

    getDepreciationStatus: builder.query<SelectOption[], void>({
      query: () => 'assets/depreciation-statuses/',
      transformResponse: (response: any[]): SelectOption[] =>
        response.map((item) => ({
          label: item.name ?? item.label,
          value: item.id ?? item.value,
        })),
      providesTags: ['DepreciationStatus'],
      keepUnusedDataFor: 300,
      refetchOnMountOrArgChange: false,
    }),

    // Depreciation Calculations
    calculateDepreciableAmount: builder.query<{ depreciable_amount: number }, { assetCost: number; residualValue: number }>({
      query: ({ assetCost, residualValue }) => 
        `assets/depreciations/calculations/calculate-depreciable-amount/?asset_cost=${assetCost}&residual_value=${residualValue}`,
    }),

    calculateAnnualDepreciation: builder.query<{ annual_depreciation: number }, { depreciableAmount: number; depreciationRate: number }>({
      query: ({ depreciableAmount, depreciationRate }) => 
        `assets/depreciations/calculations/annual-depreciation/?depreciable_amount=${depreciableAmount}&depreciation_rate=${depreciationRate}`,
    }),

    getAssetDailyDepreciationRate: builder.query<{ daily_rate: number }, string>({
      query: (assetId) => `assets/depreciations/assets/${assetId}/depreciation/daily-rate/`,
    }),

    calculateDepreciationForDate: builder.query<{ depreciation_amount: number }, { assetId: string; date: string }>({
      query: ({ assetId, date }) => `assets/depreciations/assets/${assetId}/depreciation/as-of-date/?date=${date}`,
    }),

    previewDepreciationRecalculation: builder.mutation<any, { assetId: string; parameters: Partial<DepreciationParameters> }>({
      query: ({ assetId, parameters }) => ({
        url: `assets/depreciations/assets/${assetId}/depreciation/recalculate/preview/`,
        method: 'POST',
        body: parameters,
      }),
    }),

    confirmDepreciationRecalculation: builder.mutation<any, { assetId: string; parameters: Partial<DepreciationParameters> }>({
      query: ({ assetId, parameters }) => ({
        url: `assets/depreciations/assets/${assetId}/depreciation-parameters/`,
        method: 'PUT',
        body: parameters,
      }),
      invalidatesTags: (result, error, { assetId }) => [
        { type: 'DepreciationCalculation', id: assetId },
        { type: 'NBVHistory', id: assetId },
      ],
    }),

    // Net Book Value Calculations
    calculateAssetNBV: builder.query<{ net_book_value: number; accumulated_depreciation: number; residual_value: number; asset_cost: number }, { assetId: string; asOfDate?: string }>({
      query: ({ assetId, asOfDate }) => {
        const params = asOfDate ? `?as_of_date=${asOfDate}` : '';
        return `assets/depreciations/assets/${assetId}/nbv/${params}`;
      },
      transformResponse: (response: any) => {
        if (response?.success && response?.data) {
          const data = response.data;
          return {
            net_book_value: data.netBookValue || data.calculatedNBV,
            accumulated_depreciation: data.totalDepreciation,
            residual_value: data.residualValue,
            asset_cost: data.assetCost,
          };
        }
        return response;
      },
    }),

    batchCalculateNBV: builder.mutation<any[], { asset_ids: string[]; as_of_date?: string }>({
      query: (data) => ({
        url: 'assets/depreciations/assets/nbv/batch/',
        method: 'POST',
        body: data,
      }),
    }),

    getNBVHistory: builder.query<{ results: NBVHistory[] }, { assetId?: string; page?: number; pageSize?: number; startDate?: string; endDate?: string }>({
      query: ({ assetId, page = 1, pageSize = 10, startDate, endDate }) => {
        const params = new URLSearchParams({
          page: page.toString(),
          page_size: pageSize.toString(),
        });
        if (assetId) params.append('asset_id', assetId);
        if (startDate) params.append('start_date', startDate);
        if (endDate) params.append('end_date', endDate);
        return `assets/depreciations/nbv/history/?${params}`;
      },
      providesTags: ['NBVHistory'],
      transformResponse: (response: any) => {
        if (response?.success && response?.data?.records && Array.isArray(response.data.records)) {
          return {
            results: response.data.records.map((record: any) => ({
              id: record.id,
              asset: record.assetId,
              asset_detail: {
                id: record.assetId,
                asset_tag: record.assetTag || '',
                asset_name: record.assetName || '',
              },
              calculation_date: record.calculationDate,
              net_book_value: record.netBookValue,
              accumulated_depreciation: record.totalDepreciation,
              created_at: record.createdAt,
            }))
          };
        }
        return { results: [] };
      },
    }),

    generateNBVProjection: builder.query<any[], { assetId: string; projectionYears: number }>({
      query: ({ assetId, projectionYears }) =>
        `assets/depreciations/nbv/projection/?asset_id=${assetId}&projection_years=${projectionYears}`,
      transformResponse: (response: any) => {
        if (response?.success && response?.data?.projectionPoints && Array.isArray(response.data.projectionPoints)) {
          return response.data.projectionPoints.map((point: any) => ({
            year: point.date,
            date: point.date,
            net_book_value: point.netBookValue,
            accumulated_depreciation: point.totalDepreciation,
            asset_cost: point.assetCost,
            residual_value_reached: point.residualValueReached,
          }));
        }
        // Return empty array if no data to ensure chart doesn't break
        return [];
      },
    }),

    compareActualVsProjectedNBV: builder.query<any, { assetId: string; comparisonDate: string }>({
      query: ({ assetId, comparisonDate }) => 
        `assets/depreciations/nbv/comparison/?asset_id=${assetId}&comparison_date=${comparisonDate}`,
    }),

    // Disposal Integration
    calculateDisposalAccumulatedDepreciation: builder.query<{ accumulated_depreciation: number }, { assetId: string; disposalDate: string }>({
      query: ({ assetId, disposalDate }) => 
        `assets/depreciations/assets/${assetId}/disposal/accumulated-depreciation/?disposal_date=${disposalDate}`,
    }),

    calculateDisposalGainLoss: builder.query<{ gain_loss: number }, { assetId: string; disposalDate: string; disposalValue: number }>({
      query: ({ assetId, disposalDate, disposalValue }) => 
        `assets/depreciations/assets/${assetId}/disposal/gain-loss/?disposal_date=${disposalDate}&disposal_value=${disposalValue}`,
    }),

    generateDisposalFinancialEntries: builder.mutation<any[], { assetId: string; disposalData: any }>({
      query: ({ assetId, disposalData }) => ({
        url: `assets/depreciations/assets/${assetId}/disposal/financial-entries/`,
        method: 'POST',
        body: disposalData,
      }),
    }),

    previewDisposalFinancialEntries: builder.query<any[], { assetId: string; disposalDate: string; disposalValue: number }>({
      query: ({ assetId, disposalDate, disposalValue }) => 
        `assets/depreciations/assets/${assetId}/disposal/financial-entries/preview/?disposal_date=${disposalDate}&disposal_value=${disposalValue}`,
    }),

    calculatePartialDisposal: builder.query<any, { assetId: string; disposalPercentage: number; disposalDate: string }>({
      query: ({ assetId, disposalPercentage, disposalDate }) => 
        `assets/depreciations/assets/${assetId}/disposal/partial/?disposal_percentage=${disposalPercentage}&disposal_date=${disposalDate}`,
    }),

    processPartialDisposal: builder.mutation<any, { assetId: string; disposalData: any }>({
      query: ({ assetId, disposalData }) => ({
        url: `assets/depreciations/assets/${assetId}/disposal/partial/`,
        method: 'POST',
        body: disposalData,
      }),
      invalidatesTags: ['DepreciationParameters', 'DepreciationCalculation', 'NBVHistory'],
    }),

    // Reporting and Analytics
    generateDepreciationScheduleReport: builder.mutation<DepreciationReport, any>({
      query: (filters) => ({
        url: 'assets/depreciations/reports/depreciation-schedule/',
        method: 'POST',
        body: filters,
      }),
      invalidatesTags: ['DepreciationReport'],
    }),
    refreshAnalyticsDashboard: builder.mutation<any, { client_org_id?: string; target_date?: string }>({
      query: (data) => ({
        url: 'assets/depreciations/reports/refresh/analytics-dashboard/',
        method: 'POST',
        body: {
          target_date: data?.target_date || new Date().toISOString().split('T')[0],
        },
      }),
      invalidatesTags: ['DepreciationReport'],
    }), 

    generateAccumulatedDepreciationReport: builder.mutation<DepreciationReport, any>({
      query: (filters) => ({
        url: 'assets/depreciations/reports/accumulated-depreciation/',
        method: 'POST',
        body: filters,
      }),
      invalidatesTags: ['DepreciationReport'],
    }),

    generateAnalyticsDashboard: builder.mutation<any, any>({
      query: (filters) => ({
        url: 'assets/depreciations/reports/analytics-dashboard/',
        method: 'POST',
        body: filters || {},
      }),
      transformResponse: (response: any) => {
        if (response?.success && response?.data) {
          const data = response.data;
          return {
            hasSnapshot: data.has_snapshot !== false,
            monthlyExpenseTrend: data.monthly_depreciation_trend || [],
            quarterlyExpenseTrend: data.quarterly_depreciation_trend || [],
            yearlyExpenseTrend: data.yearly_depreciation_trend || [],
            byCategory: data.depreciation_by_category || [],
            byLocation: data.depreciation_by_location || [],
            nbvPercentageDistribution: data.nbv_distribution || [],
            summary: {
              totalAssets: data.total_assets || 0,
              totalNetBookValue: data.summary_formatted.total_nbv || 0,
              totalAccumulatedDepreciation: data.summary_formatted.total_accumulated_depreciation || 0,
              fullyDepreciatedAssets: data.depreciated_assets || 0,
            },
          };
        }
        return response;
      },
    }),

// // console.log('Debug: Starting to define refreshAnalyticsDashboard endpoint');
// // Updated refresh endpoint with required parameters
//     refreshAnalyticsDashboard: builder.mutation<any, { client_org_id?: string; target_date?: string }>({
//       query: (data) => ({
//         url: 'assets/depreciations/reports/refresh/analytics-dashboard/',
//         method: 'POST',
//         body: {
//           client_org_id: data?.client_org_id || '',
//           target_date: data?.target_date || new Date().toISOString().split('T')[0],
//         },
//       }),
//         return response;
//       },
//     }),

    generateFinancialImpactAnalysis: builder.mutation<any, any>({
      query: (filters) => ({
        url: 'assets/depreciations/reports/financial-impact-analysis/',
        method: 'POST',
        body: filters,
      }),
    }),

    generateDepreciationForecast: builder.mutation<any, any>({
      query: (filters) => ({
        url: 'assets/depreciations/reports/depreciation-forecast/',
        method: 'POST',
        body: filters,
      }),
    }),

    // Export functions
    exportDepreciationSchedule: builder.mutation<Blob, any>({
      query: (filters) => ({
        url: 'assets/depreciations/reports/depreciation-schedule/export/',
        method: 'POST',
        body: filters,
        responseHandler: (response) => response.blob(),
      }),
    }),

    exportAccumulatedDepreciation: builder.mutation<Blob, any>({
      query: (filters) => ({
        url: 'assets/depreciations/reports/accumulated-depreciation/export/',
        method: 'POST',
        body: filters,
        responseHandler: (response) => response.blob(),
      }),
    }),

    exportNBVHistory: builder.mutation<Blob, any>({
      query: (filters) => ({
        url: 'assets/depreciations/nbv/history/export/',
        method: 'POST',
        body: filters,
        responseHandler: (response) => response.blob(),
      }),
    }),

    exportParametersHistory: builder.mutation<Blob, any>({
      query: (filters) => ({
        url: 'assets/depreciations/parameters/history/export/',
        method: 'POST',
        body: filters,
        responseHandler: (response) => response.blob(),
      }),
    }),

    // Import functions
    previewParametersImport: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: 'assets/depreciations/parameters/import/preview/',
        method: 'POST',
        body: formData,
      }),
    }),

    importDepreciationParameters: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: 'assets/depreciations/parameters/import/',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['DepreciationParameters', 'DepreciationCalculation', 'NBVHistory'],
    }),

    // Category-based Depreciation Management
    getCategoryDepreciationParameters: builder.query<CategoryDepreciationParameters, string>({
      query: (categoryId) => `assets/depreciations/categories/${categoryId}/depreciation-parameters/`,
      providesTags: (result, error, categoryId) => [{ type: 'CategoryDepreciation', id: categoryId }],
      transformResponse: (response: any) => {
        if (response?.success && response?.data) {
          return response.data;
        }
        return response;
      },
    }),

    updateCategoryDepreciationParameters: builder.mutation<CategoryDepreciationParameters, { categoryId: string; parameters: Partial<CategoryDepreciationParameters> }>({
      query: ({ categoryId, parameters }) => ({
        url: `assets/depreciations/categories/${categoryId}/depreciation-parameters/`,
        method: 'PUT',
        body: parameters,
      }),
      invalidatesTags: (result, error, { categoryId }) => [
        { type: 'CategoryDepreciation', id: categoryId },
        'DepreciationParameters',
      ],
    }),

    getCategoryDepreciationSummary: builder.query<CategoryDepreciationSummary, string>({
      query: (categoryId) => `assets/depreciations/categories/${categoryId}/depreciation-summary/`,
      providesTags: (result, error, categoryId) => [{ type: 'CategoryDepreciation', id: categoryId }],
      transformResponse: (response: any) => {
        if (response?.success && response?.data) {
          return response.data;
        }
        return response;
      },
    }),

    previewCategoryDefaultsApplication: builder.mutation<any, { categoryId: string; asset_ids?: string[] }>({
      query: ({ categoryId, asset_ids }) => ({
        url: `assets/depreciations/categories/${categoryId}/preview-defaults/`,
        method: 'POST',
        body: { asset_ids },
      }),
    }),

    applyCategoryDefaultsToAssets: builder.mutation<CategoryDefaultsApplication, { categoryId: string; asset_ids?: string[]; override_existing?: boolean }>({
      query: ({ categoryId, asset_ids, override_existing }) => ({
        url: `assets/depreciations/categories/${categoryId}/apply-defaults/`,
        method: 'POST',
        body: { asset_ids, override_existing },
      }),
      invalidatesTags: ['DepreciationParameters', 'DepreciationCalculation', 'NBVHistory'],
    }),

    getAssetEffectiveDepreciationParameters: builder.query<EffectiveDepreciationParameters, string>({
      query: (assetId) => `assets/depreciations/assets/${assetId}/effective-parameters/`,
      providesTags: (result, error, assetId) => [{ type: 'DepreciationParameters', id: assetId }],
    }),
  }),
});

// Export hooks
export const {
  // Asset Parameters Management
  useGetAssetDepreciationParametersQuery,
  useUpdateAssetDepreciationParametersMutation,
  useBulkUpdateDepreciationParametersMutation,
  usePreviewBulkDepreciationUpdateMutation,
  useGetDepreciationParametersHistoryQuery,
  useGetDepreciationStatusQuery,

  // Depreciation Calculations
  useCalculateDepreciableAmountQuery,
  useCalculateAnnualDepreciationQuery,
  useGetAssetDailyDepreciationRateQuery,
  useCalculateDepreciationForDateQuery,
  usePreviewDepreciationRecalculationMutation,
  useConfirmDepreciationRecalculationMutation,

  // Net Book Value Calculations
  useCalculateAssetNBVQuery,
  useBatchCalculateNBVMutation,
  useGetNBVHistoryQuery,
  useGenerateNBVProjectionQuery,
  useCompareActualVsProjectedNBVQuery,

  // Disposal Integration
  useCalculateDisposalAccumulatedDepreciationQuery,
  useCalculateDisposalGainLossQuery,
  useGenerateDisposalFinancialEntriesMutation,
  usePreviewDisposalFinancialEntriesQuery,
  useCalculatePartialDisposalQuery,
  useProcessPartialDisposalMutation,

  // Reporting and Analytics
  useGenerateDepreciationScheduleReportMutation,
  useGenerateAccumulatedDepreciationReportMutation,
  useGenerateAnalyticsDashboardMutation,
  useRefreshAnalyticsDashboardMutation,
  useGenerateFinancialImpactAnalysisMutation,
  useGenerateDepreciationForecastMutation,

  // Export functions
  useExportDepreciationScheduleMutation,
  useExportAccumulatedDepreciationMutation,
  useExportNBVHistoryMutation,
  useExportParametersHistoryMutation,

  // Import functions
  usePreviewParametersImportMutation,
  useImportDepreciationParametersMutation,

  // Category-based Depreciation Management
  useGetCategoryDepreciationParametersQuery,
  useUpdateCategoryDepreciationParametersMutation,
  useGetCategoryDepreciationSummaryQuery,
  usePreviewCategoryDefaultsApplicationMutation,
  useApplyCategoryDefaultsToAssetsMutation,
  useGetAssetEffectiveDepreciationParametersQuery,
} = depreciationApi;